import { festivalWindow, hasTime, toBcp47 } from '@shared/dates'
import type { WeekStartPref } from '@shared/preferences'
import type { Event } from '@shared/firestore'
import { eventDayCutoff } from '@shared/dayCutoff'

export type PlanStatus = 'going' | 'interested'
/** Where a day sits in a multi-day event, so the grid can draw one continuous bar. */
export type SpanPart = 'single' | 'start' | 'middle' | 'end'

export interface DayMark {
  /** The viewer's own plan on this day; going wins over interested. */
  mine?: { status: PlanStatus; span: SpanPart }
  /**
   * A friends' or groups' plan the viewer isn't part of. `declined` when the
   * viewer answered "not going" to every one of them on this day.
   */
  friends?: { span: SpanPart; declined: boolean }
  /** Events on this day, the viewer's first. */
  eventIds: string[]
}

export type AgendaBucket = 'week' | 'month' | 'later'

type CalendarEvent = Pick<Event, 'id' | 'type' | 'startDate' | 'endDate' | 'firstSetDay' | 'lastSetDay' | 'dayCutoffHour'>

/** The day Home's calendar and agenda start the week: 0 Sunday, 1 Monday. */
export type WeekStartsOn = 0 | 1

type WeekInfoLocale = Intl.Locale & {
  getWeekInfo?: () => { firstDay: number }
  weekInfo?: { firstDay: number }
}

/**
 * The language's own week start where the browser knows it (`getWeekInfo`, or
 * the older `weekInfo` getter), else Sunday for en_US and Monday elsewhere.
 * Saturday-first locales get Monday — the calendar only lays out those two.
 */
export function localeWeekStart(locale: string): WeekStartsOn {
  try {
    const l = new Intl.Locale(toBcp47(locale)) as WeekInfoLocale
    const firstDay = l.getWeekInfo?.().firstDay ?? l.weekInfo?.firstDay
    if (firstDay === 7) return 0
    if (firstDay !== undefined) return 1
  } catch {
    // Unparseable tag; fall through.
  }
  return locale.startsWith('en_US') ? 0 : 1
}

/**
 * The phone's first weekday (1 Sunday … 7 Saturday, as iOS and Android number
 * it) as a calendar week start, or null when the shell didn't report one.
 */
export function weekStartFromFirstWeekday(day: unknown): WeekStartsOn | null {
  if (typeof day !== 'number' || !Number.isInteger(day) || day < 1 || day > 7) return null
  return day === 1 ? 0 : 1
}

/** `device` is the phone's setting, which `auto` prefers over the app language. */
export function resolveWeekStart(pref: WeekStartPref, locale: string, device: WeekStartsOn | null = null): WeekStartsOn {
  if (pref === 'monday') return 1
  if (pref === 'sunday') return 0
  return device ?? localeWeekStart(locale)
}

/** Element id of an event's Home agenda row, so the calendar can scroll to it. */
export const agendaRowId = (eventId: string) => `agenda-${eventId}`

export function addDays(day: string, n: number): string {
  const [y, m, d] = day.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10)
}

function weekday(day: string): number {
  const [y, m, d] = day.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay()
}

export function startOfWeek(day: string, weekStartsOn: WeekStartsOn): string {
  return addDays(day, -((weekday(day) - weekStartsOn + 7) % 7))
}

/** Whole weeks covering the month `day` falls in, padded with the neighbouring months' days. */
export function monthGrid(day: string, weekStartsOn: WeekStartsOn): string[] {
  const first = `${day.slice(0, 7)}-01`
  const nextMonth = addDays(first, 32).slice(0, 7)
  const days: string[] = []
  for (let d = startOfWeek(first, weekStartsOn); d.slice(0, 7) !== nextMonth || days.length % 7 !== 0; d = addDays(d, 1)) {
    days.push(d)
  }
  return days
}

export function shiftMonth(day: string, n: number): string {
  const [y, m] = day.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1 + n, 1)).toISOString().slice(0, 10)
}

/**
 * The calendar days an event occupies. Festivals widen to their set days. A
 * night that ends before the cutoff (Sat 22:00 → Sun 04:00) sits on its start
 * day only — the same late-night convention as `festivalDay()`.
 */
export function eventCalendarDays(event: CalendarEvent): string[] {
  const { startDate, endDate } = event.type === 'festival' ? festivalWindow(event) : event
  const first = startDate.slice(0, 10)
  let last = (endDate ?? startDate).slice(0, 10)
  if (last > first && endDate && hasTime(endDate) && Number(endDate.slice(11, 13)) < eventDayCutoff(event)) {
    last = addDays(last, -1)
  }
  const days: string[] = []
  for (let d = first; d <= last; d = addDays(d, 1)) days.push(d)
  return days
}

function spanPart(i: number, length: number): SpanPart {
  if (length === 1) return 'single'
  if (i === 0) return 'start'
  return i === length - 1 ? 'end' : 'middle'
}

/**
 * Per-day marks for the plans calendar: the viewer's events, then friends' events
 * they aren't in. `declinedIds` are friends' events the viewer said no to.
 */
export function buildDayMarks(
  mine: { event: CalendarEvent; status: PlanStatus }[],
  friends: CalendarEvent[],
  declinedIds: ReadonlySet<string> = new Set(),
): Map<string, DayMark> {
  const marks = new Map<string, DayMark>()
  const at = (day: string) => {
    let mark = marks.get(day)
    if (!mark) marks.set(day, (mark = { eventIds: [] }))
    return mark
  }
  for (const { event, status } of mine) {
    const days = eventCalendarDays(event)
    days.forEach((day, i) => {
      const mark = at(day)
      mark.eventIds.push(event.id)
      // Going wins over interested; at the same status a multi-day band wins over a
      // single day, so a one-day plan inside a weekend doesn't cut the weekend's band.
      const span = spanPart(i, days.length)
      if (!mark.mine || (mark.mine.status === 'interested' && status === 'going')
        || (mark.mine.status === status && mark.mine.span === 'single' && span !== 'single')) {
        mark.mine = { status, span }
      }
    })
  }
  for (const event of friends) {
    const days = eventCalendarDays(event)
    days.forEach((day, i) => {
      const mark = at(day)
      mark.eventIds.push(event.id)
      // A multi-day bar reads better than a dot when both land on one day.
      const declined = declinedIds.has(event.id) && (mark.friends?.declined ?? true)
      const span = !mark.friends || mark.friends.span === 'single' ? spanPart(i, days.length) : mark.friends.span
      mark.friends = { span, declined }
    })
  }
  return marks
}

/** This week / rest of this month / later. An event already underway counts from today. */
export function agendaBucket(event: CalendarEvent, today: string, weekStartsOn: WeekStartsOn): AgendaBucket {
  const start = eventCalendarDays(event)[0] ?? today
  const from = start < today ? today : start
  if (from <= addDays(startOfWeek(today, weekStartsOn), 6)) return 'week'
  return from.slice(0, 7) === today.slice(0, 7) ? 'month' : 'later'
}
